import { AppShell, type NavGroup } from '@ps/ui-kit/components/app-shell';
import { LoginScreen } from '@ps/ui-kit/components/login-screen';
import { ErrorState, Loading } from '@ps/ui-kit/components/states';
import { useSession } from '@ps/ui-kit/components/use-session';
import { matchPath, usePath } from '@ps/ui-kit/lib/route';
import { BackLink } from '@ps/ui-kit/components/back-link';
import { useWork, workCounts } from '@ps/ui-internal/work/work-badges';
import { ApplicationDetailView } from '@ps/ui-internal/onboarding/application-detail';
import { InstructionDetail } from '@ps/ui-internal/console/instruction-detail';
import { ReconcileList } from '@ps/ui-internal/console/reconcile-list';
import { ApplicationQueue } from '@ps/ui-internal/onboarding/queue';
import { CustomerList, CustomerView } from './features/customers';

const APP = 'admin' as const;
const NAV: NavGroup[] = [{ label: 'Onboarding', items: [{ key: '/', label: 'Application queue' }] }, { label: 'Customers', items: [{ key: '/customers', label: 'Customer master' }] },
  { label: 'Payments', items: [{ key: '/reconcile', label: 'Payments to reconcile' }] }];
const roleLabel = (roles: string[]) => roles.map(r => r.replaceAll('_', ' ').toLowerCase()).join(' · ');

export function App() {
  const session = useSession(APP);
  const [path, go] = usePath();
  const work = useWork(APP, !!session.user);

  if (session.isChecking) return <Loading label="Checking your session…" />;
  if (session.checkError) return <div className="p-6"><ErrorState message="We couldn't reach PayStreet." onRetry={session.retryCheck} /></div>;
  if (!session.user) {
    return (
      <LoginScreen app={APP} title="Admin & Onboarding" onSignedIn={session.signedIn}
        eyebrow="PayStreet · Admin & Onboarding" headline="Onboard every customer with every party screened and every decision recorded."
        points={['Every party in the ownership graph screened before onboarding', 'Four-eyes approval enforced by the server and the database', 'A tamper-evident audit trail on every application']} />
    );
  }

  const user = session.user;
  const role = roleLabel(user.roles);
  // Menu badges: what is waiting for this person (loaded with the page; no polling, ADR-112).
  const counts = workCounts(work.data?.items, APP);
  const nav = NAV.map(g => ({ ...g, items: g.items.map(i => ({ ...i, count: counts[i.key]?.count, urgent: counts[i.key]?.urgent })) }));
  const appMatch = matchPath('/applications/:id', path);
  const custMatch = matchPath('/customers/:id', path);
  const insMatch = matchPath('/instructions/:id', path);
  const active = path.startsWith('/customers') ? '/customers' : path === '/reconcile' || insMatch ? '/reconcile' : '/';
  return (
    <AppShell wide host="admin.paystreet.io" nav={nav} active={active} onNavigate={go} onSignOut={session.signOut} signingOut={session.signingOut} signOutError={session.signOutError}
      user={{ displayName: user.displayName, detail: role || undefined }}>
      {insMatch ? <><BackLink label="Payments to reconcile" to="/reconcile" go={go} /><InstructionDetail app="admin" id={insMatch.id!} canReconcile={user.roles.some(r => r === 'OPS_MANAGER' || r === 'COMPLIANCE_OFFICER')} /></>
        : path === '/reconcile' ? <><h1 className="mb-6 font-display text-xl font-bold text-ink">Payments to reconcile</h1><ReconcileList app="admin" onOpen={id => go(`/instructions/${id}`)} /></>
        : appMatch ? <><BackLink label="Application queue" to="/" go={go} /><ApplicationDetailView app={APP} id={appMatch.id!} roleLabel={role} /></>
        : custMatch ? <><BackLink label="Customer master" to="/customers" go={go} /><CustomerView id={custMatch.id!} canAmend={user.roles.some(r => r === 'OPS_MANAGER' || r === 'COMPLIANCE_OFFICER')} canRecord={user.roles.includes('OPS_MANAGER')} /></>
          : path === '/customers' ? <CustomerList onOpen={id => go(`/customers/${id}`)} />
            : <ApplicationQueue app={APP} me={user.id} canAssign={user.roles.some(r => r === 'ANALYST_L1' || r === 'COMPLIANCE_OFFICER')} onOpen={id => go(`/applications/${id}`)} />}
    </AppShell>
  );
}
